import axios, { type AxiosInstance, type AxiosResponse } from 'axios'

// ── Types ───────────────────────────────────────────────────────────

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

export interface SyncRequest {
  account_id: string
}

export interface OnboardingData {
  display_name: string
  email: string
  accounts: Array<{
    tenant_type: 'm365' | 'gws'
    upn: string
    credentials: Record<string, string>
  }>
  household_members: Array<{
    name: string
    email: string
    relation: string
  }>
  autonomy_profile: {
    default_tier: number
    channels: string[]
    tier0_hold_for_approval: string[]
    tier0_delay_seconds: number
  }
}

// ── Axios Instance ──────────────────────────────────────────────────

export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

// ── Request Interceptor ─────────────────────────────────────────────

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('dw_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ── Response Interceptor ────────────────────────────────────────────

// A 401 from ANY apiClient call used to wipe dw_token and hard-redirect to
// '/' here, unconditionally. That bounced a client off /client the instant a
// single read (e.g. concierge home) 401'd, before they could ever reach
// "Set more tripwires?" — session teardown must not be a side-effect of one
// failed request. Callers already render an honest error for a failed fetch
// (ClientDashboard's homeError, HomeTripwiresCard's error sheet); a truly
// expired session is torn down explicitly by authStore's login/restoreSession,
// which call getMe() themselves and logout() on failure. So this interceptor
// only needs to pass the error through.
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => Promise.reject(error),
)

// ── Auth API ────────────────────────────────────────────────────────
//
// NOTE (TASKS/fix-frontend-auth.md): analyst/admin auth lives at
// /auth/v2/* with NO /api/v1 prefix (see backend app/main.py — auth_v2 is
// mounted unprefixed because OAuth redirect URIs are fixed URLs). It is the
// only place the frontend must step outside the /api/v1 baseURL, so we use
// an absolute URL for these two calls rather than restructuring the client.
// The old login() posted to /api/v1/auth/login, which does not exist.

export interface AnalystLoginRequest {
  email: string
  password: string
  // Optional (F-05): an UNENROLLED analyst has no authenticator yet, so they
  // present email + password ALONE and the backend routes them into TOTP
  // enrolment (a 200 carrying mfa_enrolment_required — NOT a session). An
  // already-enrolled analyst supplies the 6-digit code; omitting it fails
  // closed as invalid credentials. When present it must be exactly 6 digits.
  totp_code?: string
  // BRIEF-B4: the code texted to the mobile given at sign-up, sent only after
  // the backend answered `sign_in_code_required`.
  phone_code?: string
  // BRIEF-B16: the business chosen after the backend answered
  // `business_choice_required` (one email, several businesses). Sent on
  // every later step of the same sign-in so it runs for that business only.
  business_id?: string
}

export interface AnalystLoginResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  principal: import('../types').Principal
  is_new_account: boolean
}

// F-05: password OK but TOTP not enrolled. Returned with HTTP 200 (a distinct,
// additive shape — NOT a session). `enrolment_token` is a single-use, 15-minute
// grant that reaches ONLY the two TOTP-setup endpoints. The login page routes
// to the enrolment step and completes login via verify-setup, which mints the
// real session. Mirrors backend AnalystEnrolmentRequiredResponse.
export interface AnalystEnrolmentRequiredResponse {
  mfa_enrolment_required: true
  enrolment_token: string
  token_type: string // 'totp_enrol_grant'
  expires_in: number
}

// BRIEF-B4: an emailed link set this password before an authenticator was
// enrolled, so it opens nothing until the code texted to the mobile given at
// sign-up comes back with it. NOT a session and NOT an enrolment grant.
export interface AnalystSignInCodeRequiredResponse {
  sign_in_code_required: true
  expires_in_minutes: number
}

// BRIEF-B16: the password was right for more than one business on this
// email. Only their names come back; NOT a session, grant or texted code.
export interface AnalystBusinessChoiceRequiredResponse {
  business_choice_required: true
  businesses: { id: string; name: string }[]
}

// BRIEF-B16: a business was chosen and its authenticator code comes next,
// as its own step. NOT a session; the code typed before the choice is never
// sent again.
export interface AnalystAuthenticatorCodeRequiredResponse {
  authenticator_code_required: true
}

export type AnalystLoginResult =
  | AnalystLoginResponse
  | AnalystEnrolmentRequiredResponse
  | AnalystSignInCodeRequiredResponse
  | AnalystBusinessChoiceRequiredResponse
  | AnalystAuthenticatorCodeRequiredResponse

export function isAuthenticatorCodeRequired(
  result: AnalystLoginResult,
): result is AnalystAuthenticatorCodeRequiredResponse {
  return (
    (result as AnalystAuthenticatorCodeRequiredResponse).authenticator_code_required === true
  )
}

export function isBusinessChoiceRequired(
  result: AnalystLoginResult,
): result is AnalystBusinessChoiceRequiredResponse {
  return (
    (result as AnalystBusinessChoiceRequiredResponse).business_choice_required === true
  )
}

export function isSignInCodeRequired(
  result: AnalystLoginResult,
): result is AnalystSignInCodeRequiredResponse {
  return (result as AnalystSignInCodeRequiredResponse).sign_in_code_required === true
}

// Discriminate the two 200 shapes login can return. The enrolment shape carries
// mfa_enrolment_required:true and no access_token; a session carries an
// access_token and no such flag.
export function isEnrolmentRequired(
  result: AnalystLoginResult,
): result is AnalystEnrolmentRequiredResponse {
  return (
    (result as AnalystEnrolmentRequiredResponse).mfa_enrolment_required === true
  )
}

export async function analystLogin(
  body: AnalystLoginRequest,
): Promise<AnalystLoginResult> {
  // Omit totp_code entirely when blank so the backend's optional field is
  // honoured (an unenrolled analyst sends email + password only). A 6-digit
  // string, when present, is passed through for strict verification.
  const payload: Record<string, string> = {
    email: body.email,
    password: body.password,
  }
  if (body.totp_code) {
    payload.totp_code = body.totp_code
  }
  if (body.phone_code) {
    payload.phone_code = body.phone_code
  }
  if (body.business_id) {
    payload.business_id = body.business_id
  }

  const response = await axios.post<AnalystLoginResult>(
    `${API_BASE_URL}/auth/v2/analyst/login`,
    payload,
    { headers: { 'Content-Type': 'application/json' }, timeout: 30000 },
  )
  return response.data
}

// ── First password (public forgot-password) ─────────────────────────
//
// POST /auth/v2/analyst/forgot-password — the same unprefixed /auth/v2 mount
// as analystLogin (NO /api/v1). The backend ALWAYS answers with a generic 200
// whether or not the email maps to an account — it never enumerates. A 200
// here therefore means only "request accepted", never "account exists", and
// callers must keep their success copy identical for every address.
export async function forgotAnalystPassword(email: string): Promise<unknown> {
  const response = await axios.post(
    `${API_BASE_URL}/auth/v2/analyst/forgot-password`,
    { email },
    { headers: { 'Content-Type': 'application/json' }, timeout: 30000 },
  )
  return response.data
}

// POST /auth/v2/analyst/reset-password — redeems the emailed set-password
// token (64 hex chars) with a new password (min 12, confirmed). Same
// unprefixed /auth/v2 mount as forgotAnalystPassword (NO /api/v1). A 400
// (bad/expired token, weak or mismatched password) propagates as a thrown
// axios error for the caller to render honestly. Success returns NO session:
// the analyst must sign in at /login with the new password — never store
// anything from this response.
export async function resetAnalystPassword(
  token: string,
  newPassword: string,
  confirmPassword: string,
): Promise<unknown> {
  const response = await axios.post(
    `${API_BASE_URL}/auth/v2/analyst/reset-password`,
    { token, new_password: newPassword, confirm_password: confirmPassword },
    { headers: { 'Content-Type': 'application/json' }, timeout: 30000 },
  )
  return response.data
}

// ── TOTP enrolment (F-05) ───────────────────────────────────────────
//
// The two setup endpoints authorise EITHER a full staff session OR a scoped
// enrolment grant, passed as a BEARER token in the Authorization header (the
// backend's oauth2_scheme dependency). During first-login enrolment the caller
// holds only the grant from the login response, so these helpers send it
// explicitly rather than relying on the stored-session interceptor. Both are
// mounted unprefixed at /auth/v2/* (no /api/v1), like analystLogin.

export interface TOTPSetupResponse {
  secret: string // base32 secret for manual entry into an authenticator app
  qr_code_uri: string // otpauth:// URI (render as QR or expose as fallback)
  backup_codes: string[] // 10 single-use recovery codes, shown once
}

// Initiate setup: generates a fresh secret + backup codes (TOTP not enabled
// until verify-setup). A 403 here means the grant is expired/spent — the caller
// must send the analyst back to the password step.
export async function totpSetup(
  enrolmentToken: string,
): Promise<TOTPSetupResponse> {
  const response = await axios.post<TOTPSetupResponse>(
    `${API_BASE_URL}/auth/v2/analyst/totp/setup`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${enrolmentToken}`,
      },
      timeout: 30000,
    },
  )
  return response.data
}

// Verify the 6-digit code to enable TOTP. On the grant path the backend BURNS
// the grant (single-use) and returns a full session (AnalystLoginResponse) —
// this is the one place password → TOTP → session completes for an unenrolled
// analyst. A wrong code returns 401 with the grant INTACT (retry is fine); an
// expired/already-burned grant returns 403 (start over).
export async function totpVerifySetup(
  enrolmentToken: string,
  code: string,
): Promise<AnalystLoginResponse> {
  const response = await axios.post<AnalystLoginResponse>(
    `${API_BASE_URL}/auth/v2/analyst/totp/verify-setup`,
    { code },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${enrolmentToken}`,
      },
      timeout: 30000,
    },
  )
  return response.data
}

export async function getMe(): Promise<import('../types').Principal> {
  const response = await apiClient.get('/auth/me')
  return response.data
}

// ── Cases API ───────────────────────────────────────────────────────

export async function getCases(params?: {
  status?: string
  severity?: number
  page?: number
  page_size?: number
}): Promise<PaginatedResponse<import('../types').Case>> {
  const response = await apiClient.get('/cases', { params })
  // The /cases endpoint returns a BARE ARRAY, not a paginated envelope.
  // Normalise here so every caller gets the PaginatedResponse shape this
  // function's type promises (callers rely on response.items).
  const data = response.data
  if (Array.isArray(data)) {
    return {
      items: data,
      total: data.length,
      page: params?.page ?? 1,
      page_size: params?.page_size ?? data.length,
    }
  }
  return data
}

export async function getCase(id: string): Promise<import('../types').Case> {
  const response = await apiClient.get(`/cases/${id}`)
  return response.data
}

export async function updateCaseStatus(
  id: string,
  status: string,
): Promise<import('../types').Case> {
  const response = await apiClient.patch(`/cases/${id}`, { status })
  return response.data
}

export async function getCaseTimeline(id: string): Promise<import('../types').TimelineEvent[]> {
  const response = await apiClient.get(`/cases/${id}/timeline`)
  return response.data
}

export async function getCaseSignals(id: string): Promise<import('../types').Signal[]> {
  const response = await apiClient.get(`/cases/${id}/signals`)
  return response.data
}

// Full incident lifecycle (read-only): triggering signals, L1/L2/L3
// investigation with every tool call + result, alerts, audit trail.
export async function getCaseLifecycle(
  id: string,
): Promise<import('../types').CaseLifecycle> {
  const response = await apiClient.get(`/cases/${id}/lifecycle`)
  return response.data
}

// ── Alerts API ──────────────────────────────────────────────────────

export async function getAlerts(params?: {
  status?: string
  page?: number
  page_size?: number
}): Promise<PaginatedResponse<import('../types').Alert>> {
  const response = await apiClient.get('/alerts', { params })
  // The /alerts endpoint returns a BARE ARRAY, not a paginated envelope.
  // Normalise here so every caller gets the PaginatedResponse shape this
  // function's type promises (callers rely on response.items). Mirrors
  // getCases' normaliser above.
  const data = response.data
  if (Array.isArray(data)) {
    return {
      items: data,
      total: data.length,
      page: params?.page ?? 1,
      page_size: params?.page_size ?? data.length,
    }
  }
  if (data == null) {
    return { items: [], total: 0, page: 1, page_size: 0 }
  }
  return data
}

export async function approveAlert(alertId: string): Promise<import('../types').Alert> {
  const response = await apiClient.post(`/alerts/${alertId}/approve`)
  return response.data
}

export async function rejectAlert(alertId: string): Promise<import('../types').Alert> {
  const response = await apiClient.post(`/alerts/${alertId}/reject`)
  return response.data
}

export async function dismissAlert(alertId: string): Promise<import('../types').Alert> {
  const response = await apiClient.post(`/alerts/${alertId}/dismiss`)
  return response.data
}

// ── This-poll email-rule forwards (DW-ITEM21-THIS-POLL-FE) ──────────
//
// GET /alerts/this-poll/email-rule-forwards → backend
// app/routers/alerts.py::list_this_poll_email_rule_forwards. Rows are
// email_rule_forward_surfaced evidence from the CURRENT ingest+correlate
// cycle only — never the historical store. An empty list means BLIND
// (nothing surfaced this poll); it is NOT "no forwarding rules exist" and
// callers must render nothing for it, never a calm all-clear.
//
// Read-only and DISARMED on purpose: no approve/reject helpers exist for
// this surface — it is evidence for the analyst's eyes, not a verdict.

export interface ThisPollForwardTarget {
  address?: string | null
  // true = external, false = internal, null/absent = externality unknown
  external?: boolean | null
}

export interface ThisPollForwardEvidenceItem {
  rule_id?: string | null
  rule_name?: string | null
  enabled?: boolean | null
  new_targets?: ThisPollForwardTarget[]
}

export interface ThisPollEmailRuleForward {
  signal_id: string
  account_id: string | null
  occurred_at: string
  evidence: ThisPollForwardEvidenceItem[]
  external_determinable?: boolean | null
  note?: string | null
}

export async function getThisPollEmailRuleForwards(): Promise<
  ThisPollEmailRuleForward[]
> {
  const response = await apiClient.get('/alerts/this-poll/email-rule-forwards')
  return Array.isArray(response.data) ? response.data : []
}

// ── This-poll OAuth consent grants (DW-ITEM21-THIS-POLL-OAUTH-GRANT-FE) ──
//
// GET /alerts/this-poll/oauth-grants → backend
// app/routers/alerts.py::list_this_poll_oauth_grants. Rows are
// oauth_grant_new_surfaced evidence from the CURRENT ingest+correlate
// cycle only — never the historical store (aged grant-state rows,
// lifetime surfaced counts). An empty list means BLIND (nothing surfaced
// this poll); it is NOT "no consent grants exist" and callers must render
// nothing for it, never a calm all-clear.
//
// Read-only and DISARMED on purpose: no approve/reject helpers exist for
// this surface — it is evidence for the analyst's eyes, not a verdict.

export interface ThisPollOauthGrantEvidenceItem {
  app_id?: string | null
  app_name?: string | null
  // null/absent publisher with publisher_determinable=false means the
  // provider did not return one — publisher unknown stays unknown.
  publisher?: string | null
  publisher_determinable?: boolean | null
  scopes?: string[]
}

export interface ThisPollOauthGrant {
  signal_id: string
  account_id: string | null
  occurred_at: string
  evidence: ThisPollOauthGrantEvidenceItem[]
  note?: string | null
}

export async function getThisPollOauthGrants(): Promise<ThisPollOauthGrant[]> {
  const response = await apiClient.get('/alerts/this-poll/oauth-grants')
  return Array.isArray(response.data) ? response.data : []
}

// ── Accounts API ────────────────────────────────────────────────────

export async function getAccounts(): Promise<import('../types').MonitoredAccount[]> {
  const response = await apiClient.get('/accounts')
  return response.data
}

export async function validateAccount(params: {
  tenant_type: 'm365' | 'gws'
  upn: string
  credentials: Record<string, string>
}): Promise<{ valid: boolean; message?: string }> {
  const response = await apiClient.post('/accounts/validate', params)
  return response.data
}

export async function triggerSync(request: SyncRequest): Promise<{ status: string }> {
  const response = await apiClient.post('/accounts/sync', request)
  return response.data
}

// ── Account creation (DW-FE-ACCOUNTS-CREATE) ────────────────────────
//
// POST /accounts → backend app/routers/accounts.py::add_account (admin-gated).
// The body mirrors MonitoredAccountCreate (app/schemas.py) EXACTLY — no
// invented fields (there is no platform / email / credentials_encrypted /
// metadata / risk_score). Two schema truths shape this helper:
//
//   1. tenant_id is REQUIRED by Pydantic but the SERVER IGNORES the body value
//      and scopes the account to the authenticated admin's own tenant. We send
//      the caller's tenant_id purely to satisfy validation — this is NOT a
//      multi-tenant create and the FE must never offer a tenant picker.
//   2. auth_credentials is the raw provider credential dict; the backend
//      Fernet-encrypts it at rest. The response (MonitoredAccountResponse)
//      carries NO credentials blob (CyberCX F-07) — we never store or echo one.
//
// tenant_type is the provider slug the schema validates against
// (microsoft365 / google_workspace), not the wizard's short 'm365'/'gws'.
export interface CreateAccountRequest {
  tenant_id: string
  principal_id: string
  tenant_type: 'microsoft365' | 'google_workspace'
  tenant_provider_id?: string
  upn: string
  display_name?: string
  status?: string
  auth_credentials?: Record<string, unknown>
  monitoring_level?: string
  monitoring_until?: string
}

export async function createAccount(
  body: CreateAccountRequest,
): Promise<import('../types').MonitoredAccount> {
  const response = await apiClient.post('/accounts', body)
  return response.data
}

// ── Account devices (DW-FE-ACCOUNT-DEVICES) ─────────────────────────
//
// GET /accounts/{id}/devices → backend app/routers/accounts.py::
// list_account_devices (auth-gated, tenant-scoped, newest last_seen first).
// This read-only surface lets an operator SEE that the device registry is
// actually recording devices — otherwise the defect stays invisible.
//
// The wire model (AccountDeviceResponse) carries a `fingerprint: dict` — a raw
// provider payload that may hold noisy/sensitive material. We NEVER let it reach
// the app: this helper projects each row down to the explicit safe allow-list
// (the AccountDevice type) so `fingerprint` is dropped at the client boundary
// and can never be dumped into the DOM, even if a caller renders the object
// wholesale. Same defensive stance getCanaryTokens/MonitoredAccount take.
export async function getAccountDevices(
  accountId: string,
): Promise<import('../types').AccountDevice[]> {
  const response = await apiClient.get(`/accounts/${accountId}/devices`)
  const rows: unknown[] = Array.isArray(response.data) ? response.data : []
  return rows.map((row) => {
    const d = (row ?? {}) as Record<string, unknown>
    return {
      id: String(d.id ?? ''),
      account_id: String(d.account_id ?? ''),
      device_id: d.device_id == null ? null : String(d.device_id),
      trust_score: typeof d.trust_score === 'number' ? d.trust_score : 0,
      first_seen: d.first_seen == null ? '' : String(d.first_seen),
      last_seen: d.last_seen == null ? null : String(d.last_seen),
      is_revoked: Boolean(d.is_revoked),
    }
  })
}

// ── Canary tokens (DW-FE-CANARY-ADMIN) ──────────────────────────────
//
// GET /canary/tokens → backend app/routers/canary.py::get_tokens (analyst/
// admin-gated, tenant-scoped). LIST-ONLY on purpose:
//
//   - The create path (POST /canary/tokens) mints a real token at
//     canarytokens.org and the useful artifact — the trigger URL/key — comes
//     back ONLY in the secret `canary_token`/`canary_url` fields. This brief
//     forbids rendering those (they are the capability secret), so a console
//     "deploy" that cannot safely hand back the placement URL would only orphan
//     real provider tokens. Deploying/placing a canary happens outside this
//     read-only surface; the panel documents that honestly.
//   - The test-trigger path (POST /canary/tokens/{id}/trigger) is MOCK_MODE-only
//     (403 in production), so it is not wired here.
//
// The returned rows are typed to CanaryToken, which omits every secret field
// (see types/index.ts) — the wire may still carry canary_token/canary_url, but
// they are never read into the app.
export async function getCanaryTokens(params?: {
  is_active?: boolean
}): Promise<import('../types').CanaryToken[]> {
  const response = await apiClient.get('/canary/tokens', { params })
  return response.data
}

// ── Canary placement (DW-FE-TRIPWIRE-SHEET) ─────────────────────────
//
// POST /canary/place → backend, client-auth, own tenant. Mounted UNPREFIXED
// (no /api/v1), the same exception class as /auth/v2/* above — do not call
// this through apiClient (that would hit /api/v1/canary/place and 404).
// Mints via the existing deploy_token path and returns the ONE-TIME decoy
// the client saves. The response never carries a management URL, raw
// token secret, or provider admin link — only `decoy` (copyable text/url)
// and `where_to_put_it` (one plain sentence) reach the app.
export type CanaryPlaceKind =
  | 'word_doc'
  | 'invoice_doc'
  | 'password_file'
  | 'link'

export interface CanaryPlaceResponse {
  kind: CanaryPlaceKind
  decoy: string
  where_to_put_it: string
  // DW-CLIENT-WORD-DECOY-DOWNLOAD: document kinds carry the authenticated
  // path the client GETs to download a REAL .docx decoy they then save into
  // their own Drive / OneDrive / SharePoint. We never write to their storage,
  // so this download is the entire handoff. Absent/null for kinds with no
  // file (a bare link decoy is the URL itself).
  download_url?: string | null
  // The server's suggested save-as name. The client may type their own.
  suggested_filename?: string | null
}

// DW-CLIENT-PLACE-WAITS-FOR-BEARER: dw_token is the fast path, but a fresh
// tab can rehydrate the zustand persist snapshot (dw-auth-storage) before
// authStore.restoreSession ever copies its token into dw_token — during that
// window dw_token reads empty even though the client IS signed in. Fall back
// to the same persist source restoreSession itself reads from, so a real
// session is never treated as anonymous. Exported so HomeTripwiresCard can
// check Bearer availability without POSTing.
export function getStoredBearerToken(): string | null {
  const direct = localStorage.getItem('dw_token')
  if (direct) return direct
  const raw = localStorage.getItem('dw-auth-storage')
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { state?: { token?: unknown } }
    const token = parsed?.state?.token
    return typeof token === 'string' && token ? token : null
  } catch {
    return null
  }
}

// DW-CLIENT-PLACE-USES-LOGIN-JWT: completeLogin holds a freshly-issued access
// token before it has been through getMe() (which can fail and, historically,
// wiped dw_token before this function's own storage read ever saw it). Accept
// that token directly so placement never depends on a storage read winning a
// race against authStore.login. Callers without a token in hand (e.g.
// HomeTripwiresCard) keep using the storage fallback.
export async function placeTripwire(
  kind: CanaryPlaceKind,
  bearer?: string,
  memo?: string,
): Promise<CanaryPlaceResponse> {
  const token = bearer || getStoredBearerToken()
  // Never send an unauth place: it 401s and burns the one-time decoy for
  // nothing (see HomeTripwiresCard's mount-effect, which now waits for this
  // to return non-null before ever calling placeTripwire).
  if (!token) {
    throw new Error('placeTripwire: no Bearer token available')
  }
  const response = await axios.post<CanaryPlaceResponse>(
    `${API_BASE_URL}/canary/place`,
    // memo: what the customer named the file and where they saved it (E2b).
    memo ? { kind, memo } : { kind },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      timeout: 30000,
    },
  )
  return response.data
}

// DW-CLIENT-WORD-DECOY-DOWNLOAD: fetch the .docx decoy for a placed document
// tripwire. Same unprefixed mount and Bearer handling as placeTripwire above
// — this route is JWT-gated and tenant-checked server-side, so it cannot be a
// plain <a href> the browser fetches anonymously. Returns the raw bytes; the
// caller is responsible for handing them to the client as a file.
//
// `filename` is what the client chose to call it. The server sanitises it and
// forces .docx — this never trusts the string either.
export async function downloadDecoyDoc(
  downloadUrl: string,
  filename: string,
  bearer?: string,
): Promise<Blob> {
  const token = bearer || getStoredBearerToken()
  if (!token) {
    throw new Error('downloadDecoyDoc: no Bearer token available')
  }
  // download_url arrives as a server-relative path (/canary/download/{id}).
  // Absolute URLs are passed through untouched so a future absolute form does
  // not silently become "http://host/http://host/...".
  const url = /^https?:\/\//i.test(downloadUrl)
    ? downloadUrl
    : `${API_BASE_URL}${downloadUrl}`
  const response = await axios.get(url, {
    params: { filename },
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
    timeout: 30000,
  })
  return response.data as Blob
}

// NOTE: the old runInvestigation() helper (POST /investigations) was removed:
// the endpoint does not exist on the backend, and the "Manual Action" buttons
// it powered silently did nothing while looking like a working response
// capability. Response controls are a later, gated slice (§7).

// ── Concierge API ───────────────────────────────────────────────────
//
// The client-facing "your security team" surface. Mounted at
// /api/v1/concierge/* (backend app/routers/concierge.py). Read + chat only
// on this page — the approval/explain endpoints exist but are driven from the
// alert/case surfaces, not here. If the backend is unreachable the caller
// must surface an HONEST "we couldn't check" state — never a fake all-clear
// (PRODUCT_VISION "never a fake calm").

export interface ConciergeStatus {
  // 'all_clear' | 'attention_needed' | 'could_not_look' from the backend;
  // treated as an opaque string so a new server value never crashes the page.
  status: string
  summary: string
  open_cases: number
  recent_alerts: Array<{
    headline?: string | null
    action?: string | null
    status?: string | null
    created_at?: string | null
  }>
  last_check: string
}

export interface ConciergeChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface ConciergeChatResponse {
  content: string
  principal_id: string
  sources_queried: string[]
  cost_usd: number
  latency_ms: number
}

export async function getConciergeStatus(
  principalId: string,
): Promise<ConciergeStatus> {
  const response = await apiClient.get(`/concierge/status/${principalId}`)
  return response.data
}

// The calm-home "team brief" — GET /concierge/home/{principal_id}. Distinct
// from getConciergeStatus: this is the fuller live-home paint (team roster,
// ticker lines, tripwire count) that v93 already ships. Same honesty rule as
// the rest of Concierge: an unreachable backend must read as "we couldn't
// load", never as an empty-but-successful brief.
export interface ConciergeHomeTeamMember {
  role: string
  blurb: string
}

// Tripwire posture as FACTS — counts and timestamps, never a token, a memo or
// a manage URL. `fired_count` / `last_fired_at` are optional because an engine
// deployment older than them simply omits them: missing is UNKNOWN, and the FE
// must render no fired story at all rather than turn an absent field into a
// confident "nothing has ever fired". A fire's story itself is the existing
// disarmed "We did not act." account — these numbers only say one happened.
export interface ConciergeHomeTripwires {
  active_count: number
  last_checked_at: string | null
  fired_count?: number
  last_fired_at?: string | null
}

// Where the team has actually SEEN this person sign in. `places` are short
// "City, Country" labels the backend lifted straight out of stored Graph /
// Workspace sign-in events — facts, never a model sentence. A sign-in with no
// city on the record contributes no label, so an empty list means "we hold no
// places", never "they sign in from nowhere". The FE renders these labels
// verbatim and never adds a place of its own.
export interface ConciergeHomeSigninLocations {
  observed_count: number
  places: string[]
}

// How much of the firehose the team is actually HOLDING for this client:
// "3-4 weeks of everything we already ingest — Microsoft 365 and Google
// Workspace (sign-ins, audit, directory, mail rules, consents — not sign-ins
// alone)." `days_held` is distinct calendar days inside `window_days` that our
// own stored events cover, counted by the backend off rows it holds; `kinds`
// names the event types held, which is what stops the claim quietly shrinking
// to sign-ins. `platforms` carries an entry ONLY for a platform this client
// really has — a family with no Google Workspace has no entry at all, so the
// FE must never render a platform (or a day) the backend did not send.
export interface ConciergeHomePlatformCoverage {
  platform: string
  label: string
  days_held: number
  kinds: string[]
}

export interface ConciergeHomeCoverage {
  window_days: number
  platforms: ConciergeHomePlatformCoverage[]
}

// This week's intel desk, as one HELD FACT rather than a news item. The
// product loop is "analyse that picture against known TTPs and current CTI" —
// so current CTI belongs in the same list as Microsoft 365, Google Workspace
// and the tripwires, not in a clipping beside it. `brief_date` is the date the
// desk last distilled (the STORED brief's date, not today) and
// `applied_line_count` is how many applied-work lines came out of it for this
// client — by construction the length of `ticker`, the same list the watch
// card paints, so the two can never disagree.
//
// Optional on the wire on purpose. The backend OMITS the object when there is
// no brief, or when the desk applied nothing here: missing is UNKNOWN and the
// FE must paint nothing at all, never a reassuring "0 threats this week" we
// did not verify. A date and a count are all this carries — no CVE, no
// campaign, no detection and no rule, because naming one would be this
// surface claiming a finding it did not make.
export interface ConciergeHomeIntelDesk {
  brief_date: string
  applied_line_count: number
}

// Known attacker TTPs, in the SAME picture — the other half of "analyse that
// picture against known TTPs and current CTI". The backend does not run a new
// comparison for this: the desk's known-bad indicators are already read
// against this client's own signal digests on every correlation judgement,
// and this is those stored stamps counted back. Coverage-style on purpose,
// exactly like `days_held`: `compared_count` is how many judged batches of
// their activity were actually read, `matched_count` is how many of those
// lined up, and `window_days` is the SAME window the firehose line claims so
// the two numbers on the page are about one period.
//
// Optional on the wire on purpose. The backend OMITS the object when nothing
// matched, so missing is UNKNOWN and the FE must paint nothing at all —
// never a soothing "0 known TTPs seen", which a sample of a family's activity
// could never support. Counts only: no IOC, no CVE, no campaign, no detection
// and no rule, and a match is a comparison that lined up, never a catch.
export interface ConciergeHomeKnownTtps {
  window_days: number
  compared_count: number
  matched_count: number
}

// The last sentence of the product loop, as a HELD FACT: "escalate to a dearer
// SOC agent when unsure; act only within the armed/disarmed policy". The lines
// above say what we hold and what we read it against; this says what happened
// to the part we could NOT settle. Coverage-style for the same reason
// `known_ttps` is: `judged_count` is how many batches of this client's own
// activity the judgement read in the window, `unsure_count` is how many of
// those did not close here — either handed up for a dearer read, or left open
// because the read itself did not complete, which clears nothing. Same
// `window_days` as the firehose line, so both numbers are about one period.
//
// Optional on the wire on purpose. The backend OMITS the object when the count
// is 0, so missing is UNKNOWN and the FE must paint nothing at all — never a
// reassuring "0 escalations". Counts only, and nothing here is a response:
// going to a person is not acting, and no catch or block is ever claimed.
export interface ConciergeHomeEscalateIfUnsure {
  window_days: number
  judged_count: number
  unsure_count: number
}

export interface ConciergeHome {
  status: string
  team: ConciergeHomeTeamMember[]
  ticker: string[]
  tripwires: ConciergeHomeTripwires
  pol_summary_count: number
  /** The kinds of patterns of life behind that count, already in the client's
   *  words ("the people in your world", "how your world normally runs", "who
   *  we wake"). Categories only — never a name, a place, a device or
   *  a rhythm. Absent/empty when there is no speakable kind (including a
   *  household whose only held section is the inert response preference), and
   *  home then says the count alone rather than inventing one. */
  pol_summary_kinds?: string[]
  open_question_count: number
  signin_locations: ConciergeHomeSigninLocations
  coverage: ConciergeHomeCoverage
  intel_desk?: ConciergeHomeIntelDesk | null
  known_ttps?: ConciergeHomeKnownTtps | null
  escalate_if_unsure?: ConciergeHomeEscalateIfUnsure | null
}

export async function getConciergeHome(
  principalId: string,
): Promise<ConciergeHome> {
  const response = await apiClient.get(`/concierge/home/${principalId}`)
  return response.data
}

export async function sendConciergeChat(body: {
  message: string
  principal_id?: string
  conversation_history?: ConciergeChatMessage[]
}): Promise<ConciergeChatResponse> {
  const response = await apiClient.post('/concierge/chat', body)
  return response.data
}

// The incident STORY — "a story, not an alert" (PRODUCT_VISION_agent_team.md).
// POST /concierge/explain/{case_id} returns the Concierge's plain-language
// account of what happened, what was done, and what (if anything) is next,
// in the team's voice. `explanation` is free prose (the backend does NOT return
// structured what-we-did / what-we-recommend fields), so the FE renders it as
// prose and never invents structure. An empty explanation means "no story" —
// callers fall back to the technical view, never a story-shaped placeholder.
export interface ConciergeExplanation {
  case_id: string
  case_number: string
  explanation: string
}

export async function explainIncident(
  caseId: string,
): Promise<ConciergeExplanation> {
  const response = await apiClient.post(`/concierge/explain/${caseId}`)
  return response.data
}

// ── Onboarding API ──────────────────────────────────────────────────

export async function submitOnboarding(data: OnboardingData): Promise<{ principal_id: string }> {
  const response = await apiClient.post('/onboarding', data)
  return response.data
}

// ── Analyst-Driven Onboarding API (White-Glove) ─────────────────────

export interface CreateFamilyRequest {
  family_name: string
  primary_email: string
  primary_contact_name: string
  phone?: string
  m365_domain?: string
  gws_domain?: string
  gws_admin_email?: string
  autonomy_tier: number
  alert_channels: string[]
  household_members: Array<{
    name: string
    email: string
    role: string
  }>
}

export interface CreateFamilyResponse {
  family_id: string
  tenant_slug: string
  principal_id: string
  household_id: string
  onboarding_token: string
  m365_consent_url: string | null
  gws_delegation_guide: {
    service_account_email: string
    admin_console_url: string
    steps: string[]
    scopes_copyable: string
    scopes: string[]
  } | null
}

export async function createFamily(data: CreateFamilyRequest): Promise<CreateFamilyResponse> {
  const response = await apiClient.post('/onboarding/families', data)
  return response.data
}

export interface OnboardingProgressStep {
  id: string
  label: string
  status: 'completed' | 'pending' | 'active' | 'error'
}

export interface OnboardingProgressResponse {
  steps: OnboardingProgressStep[]
  completed: number
  total: number
  percent: number
  can_go_live: boolean
}

export async function getOnboardingProgress(
  onboardingToken: string,
): Promise<OnboardingProgressResponse> {
  const response = await apiClient.get(`/onboarding/progress/${onboardingToken}`)
  return response.data
}

export interface ConnectionStatusAccount {
  account_id: string
  tenant_type: 'microsoft365' | 'google_workspace'
  upn: string
  connection_status: string
  connection_error: string | null
  last_sync_at: string | null
  updated_at: string
}

export interface ConnectionStatusResponse {
  tenant_id: string
  accounts: ConnectionStatusAccount[]
}

export async function getConnectionStatus(
  tenantId: string,
): Promise<ConnectionStatusResponse> {
  const response = await apiClient.get(`/onboarding/connection-status/${tenantId}`)
  return response.data
}

export async function regenerateM365Consent(
  accountId: string,
): Promise<{ consent_url: string; account_id: string }> {
  const response = await apiClient.post(`/onboarding/regenerate-m365-consent/${accountId}`)
  return response.data
}

export async function testGWSConnection(
  accountId: string,
): Promise<{ status: 'active' | 'error'; message: string }> {
  const response = await apiClient.post(`/onboarding/gws/test-connection/${accountId}`)
  return response.data
}

export interface GoLiveResponse {
  status: string
  tenant_id: string
  accounts_monitored: number
  platforms: string[]
  welcome_alert_id: string
  first_sync_results: Record<string, number>
}

export async function goLive(tenantId: string): Promise<GoLiveResponse> {
  const response = await apiClient.post(`/onboarding/go-live/${tenantId}`)
  return response.data
}

// ── Org-admin API (SMB Wave 2C) ─────────────────────────────────────
//
// Read-only by design: the org admin sees status and recommendations,
// never a control that acts on an account (response stays disarmed).
// A 403 here is normal and meaningful — it means this principal is not an
// owner/admin of a business tenant — so callers should surface it honestly
// rather than retrying or rendering an empty dashboard as if it were clean.

export async function getOrgOverview(): Promise<import('../types').OrgOverview> {
  const response = await apiClient.get('/org/overview')
  return response.data
}

export async function getOrgMembers(): Promise<
  import('../types').OrgMembersResponse
> {
  const response = await apiClient.get('/org/members')
  return response.data
}

// ── Patterns of Life API ────────────────────────────────────────────
//
// "What your team knows about your world." The stored, tenant-scoped
// patterns-of-life captured during onboarding (backend
// app/routers/patterns_of_life.py, GET /onboarding/patterns-of-life).
//
// This read-back also DOUBLES as the client's way to CORRECT it
// (PRODUCT_VISION: "no, that's my new accountant, not suspicious"). The
// correction router writes deterministically (no LLM) through
// POST /onboarding/patterns-of-life/{id}/correct — see correctPoLEntry.
//
// `data` shape varies by section (people vs relationships vs operations,
// etc.) so it is typed as an opaque record and narrowed in the page. We
// never surface the escalation contact_details key — sensitive personal
// data under §7 (NZ Privacy Act) that this view must not display.

// Correction provenance returned by the backend _entry_out serialiser. On an
// untouched entry correction_status is null; a client edit sets it to one of
// these three deterministic states. The FE renders the provenance ("you
// corrected this on 12 Aug") rather than faking any state locally.
export type PoLCorrectionStatus = 'corrected' | 'confirmed_normal' | 'removed'

// The client action → backend status mapping (POL_CORRECTION_ACTIONS):
//   correct        → correction_status 'corrected'      (right fact supplied)
//   not_suspicious → correction_status 'confirmed_normal' (vouched, Run-3)
//   remove         → correction_status 'removed'          (soft-deleted)
export type PoLCorrectionAction = 'correct' | 'not_suspicious' | 'remove'

export interface PatternsOfLifeEntry {
  id: string
  section: string
  principal_id: string | null
  subject: string | null
  // On a 'corrected' entry, data carries client_correction (the right fact)
  // and client_corrected=true; on a 'confirmed_normal' entry it carries
  // client_confirmed_normal=true (+ optional client_confirmation_note).
  data: Record<string, unknown>
  source: string
  created_at: string | null
  // Correction provenance (null / absent on an untouched entry). Present in
  // BOTH the GET list and the correction response, so corrected & confirmed
  // states persist honestly across reloads.
  correction_status?: PoLCorrectionStatus | null
  corrected_by?: string | null
  corrected_at?: string | null
  correction_history?: unknown[]
}

export interface PatternsOfLifeResponse {
  entries: PatternsOfLifeEntry[]
}

export async function getPatternsOfLife(): Promise<PatternsOfLifeResponse> {
  const response = await apiClient.get('/onboarding/patterns-of-life')
  return response.data
}

// The client corrects one read-back entry (read-back → edit). Returns the
// updated entry with its new provenance. 'correct' requires correction_text
// (the right fact); it is optional context for the other two actions.
export async function correctPoLEntry(
  entryId: string,
  action: PoLCorrectionAction,
  correctionText = '',
): Promise<PatternsOfLifeEntry> {
  const response = await apiClient.post(
    `/onboarding/patterns-of-life/${entryId}/correct`,
    { action, correction_text: correctionText },
  )
  return response.data
}

// ── Context Questions API (Context-Keeper) ──────────────────────────
// The "your team is asking" loop: the Context-Keeper notices a change in
// patterns of life ("a lot of new email to a domain we've not seen") and ASKS
// the client about it — never a verdict. The client answers in free text (the
// answer is folded back into patterns of life) or sets it aside. The list
// endpoint returns ONLY open questions; there is no history to render, so the
// UI must not fabricate one.
export interface ContextQuestion {
  id: string
  principal_id: string
  kind: string
  subject: string
  question_text: string
  // Measured numbers backing the question. Shape varies by `kind`; render only
  // what's present. Known keys across detectors: recent_count / prior_count /
  // window_days / prior_days / domain / example_recipient (new external
  // contact), device_family / first_seen (new device), recent_median_hour_utc /
  // prior_median_hour_utc / shift_hours / recent_signins / prior_signins (new
  // working hours).
  supporting_facts: Record<string, unknown>
  status: string
  created_at: string | null
}

export async function getContextQuestions(
  principalId?: string,
): Promise<ContextQuestion[]> {
  const response = await apiClient.get('/concierge/context-questions', {
    params: principalId ? { principal_id: principalId } : undefined,
  })
  return response.data
}

export async function answerContextQuestion(
  questionId: string,
  answerText: string,
): Promise<ContextQuestion> {
  const response = await apiClient.post(
    `/concierge/context-questions/${questionId}/answer`,
    { answer_text: answerText },
  )
  return response.data
}

export async function dismissContextQuestion(
  questionId: string,
): Promise<ContextQuestion> {
  const response = await apiClient.post(
    `/concierge/context-questions/${questionId}/dismiss`,
  )
  return response.data
}

// ── Idle Advisory Suggestions API ───────────────────────────────────
//
// The client-facing "care, not spam" surface. The team notices a REAL fact
// about the client (a pattern of life) and offers a gentle, optional next step
// tied to it — "Paula's travelling; want us to keep a closer eye while she's
// away?". Mounted at /api/v1/concierge/suggestions/* (backend
// app/routers/concierge.py). Voice rules that matter here:
// - The list endpoint returns ONLY OPEN suggestions. There is no history to
//   render, so the UI must not fabricate one; an empty list renders nothing.
// - suggestion_text is the TEAM'S voice — rendered verbatim, never rewritten,
//   and never wrapped in invented urgency chrome.
// - Accept and Decline are final. Accept records provenance + a journal note
//   and does NOT arm any response; Decline suppresses that kind for 30 days
//   (both server-side). The FE never fakes success — a failed call surfaces an
//   honest error and the offer stays put.
export interface AdvisorySuggestion {
  id: string
  principal_id: string | null
  kind: string
  subject: string
  suggestion_text: string
  // The measured pattern fact that motivates the offer. Shape varies by `kind`;
  // typed as an opaque record so a new detector never crashes the page.
  tied_to: Record<string, unknown>
  status: string
  created_at: string | null
}

export async function getAdvisorySuggestions(
  principalId?: string,
): Promise<AdvisorySuggestion[]> {
  const response = await apiClient.get('/concierge/suggestions', {
    params: principalId ? { principal_id: principalId } : undefined,
  })
  return response.data
}

export async function acceptAdvisorySuggestion(
  suggestionId: string,
  note?: string,
): Promise<AdvisorySuggestion> {
  const response = await apiClient.post(
    `/concierge/suggestions/${suggestionId}/accept`,
    note ? { note } : {},
  )
  return response.data
}

export async function declineAdvisorySuggestion(
  suggestionId: string,
  note?: string,
): Promise<AdvisorySuggestion> {
  const response = await apiClient.post(
    `/concierge/suggestions/${suggestionId}/decline`,
    note ? { note } : {},
  )
  return response.data
}

// ── Escalation Preferences API ──────────────────────────────────────
//
// The client's control over "who gets woken, for what, and how"
// (backend app/routers/escalation.py, GET/PUT /escalation/profile). This is
// the client editing THEIR OWN profile — not a staff surface.
//
// GET never errors on a first visit: the backend returns a sensible EMPTY
// shape (exists:false, defaults) with a 200 so the form renders blank. We also
// treat a 404 as "no profile yet → blank form" so an honest empty form is
// shown either way, never an error page.
//
// The phone number is E.164 and sensitive (§7 out-of-band contactability): we
// send it exactly as the backend validates it and never mask or reformat it
// behind the client's back.

export type EscalationChannel = 'app' | 'sms' | 'email'

export interface EscalationProfile {
  principal_id: string
  escalation_contact_name: string | null
  escalation_contact_phone_e164: string | null
  notify_severity_threshold: number
  three_am_call_consent: boolean
  preferred_channel: EscalationChannel
  // false until the client has saved once; the form renders identically either
  // way, so this is provenance, not a gate.
  exists: boolean
  updated_at: string | null
  updated_by: string | null
}

// The write payload mirrors the backend EscalationProfileIn exactly (the
// service layer is the source of truth for E.164 / 1–10 / enum validation).
export interface EscalationProfileUpdate {
  escalation_contact_name: string | null
  escalation_contact_phone_e164: string | null
  notify_severity_threshold: number
  three_am_call_consent: boolean
  preferred_channel: EscalationChannel
}

// The blank shape used when the client has never saved (and as the 404
// fallback). Mirrors the backend defaults: threshold 8, channel 'app',
// 3am consent off — the safe, least-intrusive defaults.
export const EMPTY_ESCALATION_PROFILE: EscalationProfile = {
  principal_id: '',
  escalation_contact_name: null,
  escalation_contact_phone_e164: null,
  notify_severity_threshold: 8,
  three_am_call_consent: false,
  preferred_channel: 'app',
  exists: false,
  updated_at: null,
  updated_by: null,
}

export async function getEscalationProfile(): Promise<EscalationProfile> {
  try {
    const response = await apiClient.get('/escalation/profile')
    return response.data
  } catch (err) {
    // A 404 means "no profile yet" — render a blank form, not an error page.
    // Any other failure is a real error the caller must surface honestly.
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return { ...EMPTY_ESCALATION_PROFILE }
    }
    throw err
  }
}

export async function putEscalationProfile(
  update: EscalationProfileUpdate,
): Promise<EscalationProfile> {
  const response = await apiClient.put('/escalation/profile', update)
  return response.data
}

// ── Onboarding Interview API ────────────────────────────────────────
//
// "The first meeting with your new security lead" (PRODUCT_VISION_agent_team.md
// "Onboarding as an INTERVIEW, not a form"). The Concierge conducts a fixed,
// six-question script; the client answers one question at a time. Backend:
// app/routers/interview.py, mounted at /api/v1/interview/*.
//
// The QUESTIONS are deterministic in the backend script — the FE renders each
// prompt EXACTLY as sent and never paraphrases (the copy is the Concierge's
// voice, and lives server-side). start is idempotent (returns the caller's
// existing in-progress session, so a closed tab resumes with no data loss).

export type InterviewStatus = 'in_progress' | 'complete' | 'abandoned'

// One step of the script as the backend serialises it (id, 1-based number,
// verbatim prompt). This is the ONLY source of a question's wording.
export interface InterviewStepPrompt {
  id: string
  number: number
  prompt: string
}

export interface InterviewScript {
  steps: InterviewStepPrompt[]
}

// A session as returned by start/resume (interview_out). `next_question` is null
// once every step is answered; `current_step` is the 1-based number the client
// left off at (resume picks up here).
export interface InterviewSession {
  id: string
  principal_id: string
  status: InterviewStatus
  current_step: number
  answered_steps: string[]
  answers: Record<string, unknown>
  next_question: InterviewStepPrompt | null
  started_at: string | null
  completed_at: string | null
}

// The answer response is a session plus a routing report and the OPT-IN
// one-line LLM `bridge` (null under MOCK_MODE / when there is no next question).
export interface InterviewAnswerResponse extends InterviewSession {
  routed: {
    patterns_of_life: boolean
    escalation_profile: boolean
    memory: boolean
  }
  bridge: string | null
}

// One step's typed answer. `answer` is that step's shape (the page composes it
// from the client's prose); `bridge` opts into the warm one-liner.
export interface InterviewAnswerRequest {
  step: string
  answer: Record<string, unknown>
  bridge?: boolean
}

export async function getInterviewScript(): Promise<InterviewScript> {
  const response = await apiClient.get('/interview/script')
  return response.data
}

// Read-only gate for client home. Prefer complete over in_progress over none.
// Never calls start — after complete, start would create a new meeting.
export type InterviewGateStatus = 'none' | 'in_progress' | 'complete'

export interface InterviewStatusResponse {
  status: InterviewGateStatus
  id: string | null
}

export async function getInterviewStatus(): Promise<InterviewStatusResponse> {
  const response = await apiClient.get('/interview/status')
  return response.data
}

export async function startInterview(): Promise<InterviewSession> {
  const response = await apiClient.post('/interview/start')
  return response.data
}

export async function getInterview(id: string): Promise<InterviewSession> {
  const response = await apiClient.get(`/interview/${id}`)
  return response.data
}

export async function answerInterview(
  id: string,
  body: InterviewAnswerRequest,
): Promise<InterviewAnswerResponse> {
  const response = await apiClient.post(`/interview/${id}/answer`, body)
  return response.data
}

export async function completeInterview(id: string): Promise<InterviewSession> {
  const response = await apiClient.post(`/interview/${id}/complete`)
  return response.data
}

// ── Admin API ───────────────────────────────────────────────────────

export async function getPrincipals(): Promise<import('../types').Principal[]> {
  const response = await apiClient.get('/admin/principals')
  return response.data
}

export async function getTenants(): Promise<import('../types').Tenant[]> {
  const response = await apiClient.get('/admin/tenants')
  return response.data
}

export interface AdminDashboardStats {
  reported_at: string
  totals: {
    tenants: number
    principals: number
    monitored_accounts: number
  }
  active_cases: number
  alerts_today: number
  ingestion: {
    accounts_synced_last_hour: number
    status: string
  }
}

// Real endpoint is /admin/dashboard (the old getStats hit a non-existent
// /admin/stats and the UI silently rendered invented numbers instead).
export async function getAdminDashboard(): Promise<AdminDashboardStats> {
  const response = await apiClient.get('/admin/dashboard')
  return response.data
}

export async function getAuditLog(): Promise<import('../types').AuditLogEntry[]> {
  const response = await apiClient.get('/admin/audit-log')
  return response.data
}

// ── Admin tenant lifecycle (DW-TENANT-LIFECYCLE) ────────────────────
//
// Calm admin controls over the LIVE backend routes (backend
// app/routers/admin.py). All four are admin-gated server-side; the FE
// mirrors the exact contract and never fakes a transition — a failed call
// surfaces an honest error and the row's status is left untouched.
//
// Fail-closed rules enforced by the backend that shape the UI:
// - suspend: only active/onboarding → suspended (409 if already inert).
// - resume:  only suspended → active (409 otherwise).
// - offboard: caller must ECHO the tenant id back as `confirm_tenant_id`
//   (a fat-finger guard, 400 on mismatch); terminal states 409.
// - export:  a metadata COUNT inventory only — never credentials/secrets.

export interface TenantSuspendResponse {
  tenant_id: string
  slug: string
  status: string
  previous_status: string
}

export interface TenantResumeResponse {
  tenant_id: string
  slug: string
  status: string
}

export interface TenantExportPackage {
  exported_at: string
  exported_by: string
  tenant: {
    id: string
    slug: string
    name: string
    status: string
    created_at: string | null
  }
  inventory: {
    principals: number
    monitored_accounts: number
    cases: number
    alerts: number
  }
  // Explicit machine-readable contract: this package carries no secrets.
  contains_credentials: boolean
}

export interface TenantOffboardResponse {
  tenant_id: string
  slug: string
  status: string
  previous_status: string
  accounts_disabled: number
}

export async function suspendTenant(
  tenantId: string,
  reason?: string,
): Promise<TenantSuspendResponse> {
  const response = await apiClient.post(
    `/admin/tenants/${tenantId}/suspend`,
    reason ? { reason } : {},
  )
  return response.data
}

export async function resumeTenant(
  tenantId: string,
  reason?: string,
): Promise<TenantResumeResponse> {
  const response = await apiClient.post(
    `/admin/tenants/${tenantId}/resume`,
    reason ? { reason } : {},
  )
  return response.data
}

export async function exportTenant(
  tenantId: string,
): Promise<TenantExportPackage> {
  const response = await apiClient.get(`/admin/tenants/${tenantId}/export`)
  return response.data
}

// `confirmTenantId` is echoed back per the backend fat-finger guard — the UI
// requires the admin to type the tenant id, and we pass exactly what they
// typed so a mismatch fails closed at the server (400) rather than silently.
export async function offboardTenant(
  tenantId: string,
  confirmTenantId: string,
  reason?: string,
): Promise<TenantOffboardResponse> {
  const response = await apiClient.post(`/admin/tenants/${tenantId}/offboard`, {
    confirm_tenant_id: confirmTenantId,
    ...(reason ? { reason } : {}),
  })
  return response.data
}

// ── The customer page (BRIEF-E2b; routes from E2a) ──────────────────
//
// One route per section under /api/v1/soc/ (backend app/routers/soc.py).
// The page renders what these say and nothing it works out itself: every
// list carries `could_not_see`, and who sees what (the owner, or a plain
// member who sees only their own account) is decided by the routes, never
// here. `scope` is optional: when /soc/today says "self" the page is a
// member's.

export interface SocNotRead {
  person: string
  reason: string
}

export interface SocToday {
  scope?: string | null
  soc_team_name: string
  day_start: string | null
  day?: string | null
  day_end: string | null
  readout: string
  read_at: string | null
  accounts_watched: string[]
  accounts_read: string[]
  accounts_not_read: SocNotRead[]
  open_questions_count: number
  response_armed: boolean
  // E3b: false when the shown read is not today's (the daily read stopped);
  // could_not_see then carries the sentence saying how old it is.
  read_is_current?: boolean
  could_not_see: string[]
}

export type SocQuestionKind = 'analyst_question' | 'context_question' | 'escalation'

export interface SocQuestion {
  kind: SocQuestionKind | string
  ref: string
  person: string | null
  assessment: string | null
  question: string | null
  asked_at: string | null
  // E5c: false for an escalation about the signed-in person's own account
  // (we reach them by text; the route refuses the answer with 403).
  answerable?: boolean
}

export interface SocNeedsYou {
  items: SocQuestion[]
  could_not_see: string[]
}

export interface SocAnswerResponse {
  recorded: boolean
  kind: string
  person: string | null
  next: string
}

export interface SocAction {
  when: string | null
  person: string
  found: string | null
  action: string
  action_words: string
  status: string
  reason: string | null
  reversible: boolean
  verified: boolean
  after_state: unknown
}

export interface SocActions {
  days: number
  response_armed: boolean
  items: SocAction[]
  could_not_see: string[]
}

export interface SocPerson {
  person: string
  account: string
  org_role: string | null
  role_facts: string[]
  last_read: string | null
  open_question: boolean
  close_watch: boolean
  what_the_soc_knows: string[]
}

export interface SocPeople {
  items: SocPerson[]
  could_not_see: string[]
}

export interface SocAccountSource {
  person: string
  account: string
  cloud: string | null
  connection: string | null
  permissions: Record<string, string[]>
  last_successful_poll: string | null
  cannot_see: string[]
  // BRIEF-E5b: "Microsoft 365" or "Google Workspace"; null for a cloud the
  // route does not name. The page shows this, never the code word in `cloud`.
  cloud_name?: string | null
}

// BRIEF-E5b: one dark-web finding in the owner's words: one line, one next step.
export interface SocDarkWebFinding {
  kind: string
  person: string
  found_on: string
  line: string
  next_step: string
  open_question: boolean
}

export interface SocDarkWebDomain {
  domain: string
  last_checked: string | null
  result: string
  searches_not_read: unknown
}

// The owner's own test of a tripwire (BRIEF-E6): a ten-minute window opened
// from the page. waiting = open, nothing arrived yet; fired = the first fire
// inside it arrived, when and from which city or country; expired = nothing
// arrived in ten minutes. Never the opener's address.
export interface SocTripwireTest {
  state: 'waiting' | 'fired' | 'expired' | string
  opened_at: string | null
  expires_at: string | null
  fired_at: string | null
  fired_from: string | null
}

export interface SocTripwire {
  id: string
  name: string | null
  kind: string | null
  where: string | null
  placed_at: string | null
  where_confirmed_at: string | null
  proved_at: string | null
  last_fired_at: string | null
  status: string
  /** Absent from a backend older than E6: the page then offers no test. */
  test?: SocTripwireTest | null
}

export interface SocSources {
  accounts: SocAccountSource[]
  threat_intelligence: {
    brief_date: string | null
    headline: string | null
    is_today: boolean
    // Real matches of our indicator lists in the activity read for the people
    // this caller may see, over the last `days` days (BRIEF-C9).
    // Absent from a backend older than C9: the card then shows nothing for it.
    applied_hits?: { count: number; people: number; days: number }
    applied: Array<{ title: string; source: string; techniques: string[] }>
  }
  dark_web: {
    domains: SocDarkWebDomain[]
    lookback_days: number
    findings_by_kind: Record<string, number>
    // BRIEF-E5b. Absent from an older backend: the page then lists none.
    findings?: SocDarkWebFinding[]
  }
  tripwires: SocTripwire[]
  could_not_see: string[]
}

export interface SocTripwirePlaced extends SocTripwire {
  proof: string
}

export interface SocToldUs {
  fact_id: string
  acknowledgement: string
}

export async function getSocToday(): Promise<SocToday> {
  const response = await apiClient.get('/soc/today')
  return response.data
}

export async function getSocNeedsYou(): Promise<SocNeedsYou> {
  const response = await apiClient.get('/soc/needs-you')
  return response.data
}

export async function answerSocQuestion(
  ref: string,
  answer: string,
  notUs = false,
): Promise<SocAnswerResponse> {
  // E5c: notUs only on an escalation ("That was not us"); the route refuses
  // it on a question (422).
  const response = await apiClient.post(
    `/soc/needs-you/${encodeURIComponent(ref)}/answer`,
    notUs ? { answer, not_us: true } : { answer },
  )
  return response.data
}

export async function getSocActions(days = 7): Promise<SocActions> {
  const response = await apiClient.get('/soc/actions', { params: { days } })
  return response.data
}

export async function getSocPeople(): Promise<SocPeople> {
  const response = await apiClient.get('/soc/people')
  return response.data
}

export async function getSocSources(): Promise<SocSources> {
  const response = await apiClient.get('/soc/sources')
  return response.data
}

// ── The day-one report (BRIEF-B19; backend app/routers/day_one.py, body
// from report_view in app/desk/day_one_report.py). Owner or admin only: a
// plain member, or a business with no audit yet, gets 404.

export interface DayOneWrongItem {
  key: string
  what: string
  what_to_do: string
  settled: boolean
  question: string | null
  not_judged?: boolean
}

export interface DayOneReport {
  /** The audit run: queued, running, done or failed. */
  status: 'queued' | 'running' | 'done' | 'failed' | string
  runs: number
  finished_at: string | null
  qualified: boolean | null
  qualified_reason: string | null
  /** written, in_progress, not_written, cap_reached, model_error, unreadable_reply, or null. */
  report_status: string | null
  /** The report text; only present once report_status is "written". */
  report: string | null
  written_at: string | null
  already_wrong: DayOneWrongItem[]
  questions: { question: string; asked: boolean }[]
  areas: Record<string, string>
}

export async function getDayOne(): Promise<DayOneReport> {
  const response = await apiClient.get('/day-one')
  return response.data
}

export async function confirmSocTripwirePlaced(
  tokenId: string,
  where: string,
): Promise<SocTripwirePlaced> {
  const response = await apiClient.post(
    `/soc/tripwires/${encodeURIComponent(tokenId)}/placed`,
    { where },
  )
  return response.data
}

// BRIEF-E6: owner or admin only (403 for a plain member), 404 for another
// business's tripwire, 409 for a switched-off one. Opening again while the
// window is open returns the same window.
export async function openSocTripwireTest(tokenId: string): Promise<SocTripwire & { line: string }> {
  const response = await apiClient.post(`/soc/tripwires/${encodeURIComponent(tokenId)}/test`)
  return response.data
}

/** One tripwire's row with the state of its test; polled while a window is open. */
export async function getSocTripwire(tokenId: string): Promise<SocTripwire> {
  const response = await apiClient.get(`/soc/tripwires/${encodeURIComponent(tokenId)}`)
  return response.data
}

export async function tellSoc(note: string): Promise<SocToldUs> {
  const response = await apiClient.post('/soc/tell-us', { note })
  return response.data
}

// ── SMB self sign-up: Google Workspace (BRIEF-B6b; routes from B6a) ──
//
// Public by design: the session token is the capability, so these use plain
// fetch and never the staff axios client (which would attach a stored token).
// Backend: app/routers/smb_onboarding.py.

export type SmbCloud = 'microsoft365' | 'google_workspace'

/** POST /smb-onboarding/{session}/consent for a Google session. */
export interface SmbGoogleConsent {
  state: string
  cloud: 'google_workspace'
  client_id: string
  scopes: string[]
  admin_console_url: string
}

// The only console a Google grant is made in. Anything else is not shown.
export const GOOGLE_ADMIN_CONSOLE_PREFIX = 'https://admin.google.com/'

/** The Google grant from a consent body, or null when it is not a well-formed one. */
export function readGoogleConsent(body: unknown): SmbGoogleConsent | null {
  if (typeof body !== 'object' || body === null) return null
  const b = body as Record<string, unknown>
  if (b.cloud !== 'google_workspace') return null
  if (typeof b.client_id !== 'string' || !b.client_id.trim()) return null
  if (
    !Array.isArray(b.scopes) ||
    b.scopes.length === 0 ||
    !b.scopes.every((s) => typeof s === 'string' && s.length > 0)
  ) {
    return null
  }
  if (
    typeof b.admin_console_url !== 'string' ||
    !b.admin_console_url.startsWith(GOOGLE_ADMIN_CONSOLE_PREFIX)
  ) {
    return null
  }
  return {
    state: typeof b.state === 'string' ? b.state : 'consent_sent',
    cloud: 'google_workspace',
    client_id: b.client_id,
    scopes: b.scopes as string[],
    admin_console_url: b.admin_console_url,
  }
}

export type SmbGoogleConfirmResult =
  | { ok: true; tenantId: string | null }
  | { ok: false; message: string }

export const SMB_GOOGLE_CONFIRM_FALLBACK =
  'That did not go through. Please try again in a moment.'

/**
 * POST /smb-onboarding/{session}/google/confirm with { admin_email }.
 * 200 → consent_completed. 409 → detail { step, message } (the step Google
 * refused at). 400 → detail is a sentence (wrong domain, duplicate, ...).
 * Whatever sentence the route returns is shown as written.
 */
export async function confirmSmbGoogle(
  session: string,
  adminEmail: string,
): Promise<SmbGoogleConfirmResult> {
  let res: Response
  try {
    res = await fetch(
      `${API_BASE_URL}/api/v1/smb-onboarding/${encodeURIComponent(session)}/google/confirm`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_email: adminEmail }),
      },
    )
  } catch {
    return { ok: false, message: SMB_GOOGLE_CONFIRM_FALLBACK }
  }
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    body = null
  }
  if (res.ok) {
    const tenant = (body as { tenant_id?: unknown } | null)?.tenant_id
    return { ok: true, tenantId: typeof tenant === 'string' ? tenant : null }
  }
  if (res.status === 422) {
    return { ok: false, message: 'Enter your Google Workspace administrator email.' }
  }
  // Only the route's own refusals carry a sentence meant for the customer.
  if (res.status !== 400 && res.status !== 409) {
    return { ok: false, message: SMB_GOOGLE_CONFIRM_FALLBACK }
  }
  const detail = (body as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string' && detail) return { ok: false, message: detail }
  if (typeof detail === 'object' && detail !== null) {
    const message = (detail as { message?: unknown }).message
    if (typeof message === 'string' && message) return { ok: false, message }
  }
  return { ok: false, message: SMB_GOOGLE_CONFIRM_FALLBACK }
}

// ── SOC lead console (BRIEF-F1b; backend F1a, /api/v1/hub/console/*) ──────
//
// Staff only (get_current_analyst). Read-only, except acknowledging a
// platform alarm. Every field is what the hub route said; the page works out
// nothing about a business on its own beyond colouring what it was given.

export interface ConsoleFeed {
  account_id: string
  upn: string
  tenant_id: string
  state: 'fresh' | 'stale' | 'dark' | 'never_synced' | 'sync_disabled' | string
  hours_since_sync: number | null
  watched: boolean
}

export interface ConsoleReadoutHeadline {
  people: number
  read: number
  not_read_or_not_seen: number
  questions: number
  escalations: number
}

export interface ConsoleBusiness {
  tenant_id: string
  name: string
  cloud: string | null
  watched: boolean
  qualified: boolean | null
  people_watched: number
  people_not_watched: number
  spent_today_usd: number
  daily_cap_usd: number
  cap_is_own: boolean
  close_watch: boolean
  close_watch_until: Record<string, string>
  response_armed: boolean
  feeds: ConsoleFeed[]
  newest_event_at: string | null
  readout_day: string
  readout_headline: ConsoleReadoutHeadline
}

export interface ConsoleQueueItem {
  episode_id: string
  tenant_id: string
  business: string
  account_id: string
  person: string | null
  upn: string | null
  status: 'ask_customer' | 'escalated' | string
  shift_kind: string | null
  opened_at: string | null
  assessment: string | null
  customer_told: boolean
  readout_sent_at: string | null
  stale: boolean
  no_handover: boolean
  owner_told_at: string | null
  woken_at: string | null
  wake_followed_up_at: string | null
  owner_not_reached_at: string | null
  backup_woken_at: string | null
}

export interface ConsoleAlarm {
  id: string
  kind: string
  subject: string | null
  day: string | null
  headline: string | null
  first_seen: string | null
  last_seen: string | null
  count: number | null
  delivered_at: string | null
  outcome: string | null
  acknowledged_at: string | null
  acknowledged_by: string | null
}

export interface ConsoleReadout {
  tenant_id: string
  readout: string
}

export async function getConsoleBusinesses(): Promise<ConsoleBusiness[]> {
  const res = await apiClient.get<ConsoleBusiness[]>('/hub/console/businesses')
  return res.data
}

export async function getConsoleQueue(): Promise<ConsoleQueueItem[]> {
  const res = await apiClient.get<ConsoleQueueItem[]>('/hub/console/queue')
  return res.data
}

export async function getConsoleReadout(tenantId: string): Promise<ConsoleReadout> {
  const res = await apiClient.get<ConsoleReadout>(
    `/hub/console/businesses/${encodeURIComponent(tenantId)}/readout`,
  )
  return res.data
}

export async function getConsoleAlarms(): Promise<ConsoleAlarm[]> {
  const res = await apiClient.get<ConsoleAlarm[]>('/hub/console/alarms')
  return res.data
}

export async function acknowledgeConsoleAlarm(alarmId: string): Promise<ConsoleAlarm> {
  const res = await apiClient.post<ConsoleAlarm>(
    `/hub/console/alarms/${encodeURIComponent(alarmId)}/ack`,
  )
  return res.data
}

// BRIEF-F2: staff set a business's daily cap and close watch from the
// console (the same limits as the server command; the server says why when
// it refuses). Nothing here touches response or arming.

export interface ConsoleCapSet {
  tenant: string
  daily_cap_usd: number
  was: number
}

export interface ConsoleCloseWatchSet {
  tenant: string
  close_watch_until: string
  account_id: string
}

export interface ConsoleCloseWatchEnded {
  tenant: string
  ended: string
  was: string
  account_id: string
}

export async function setConsoleCap(tenantId: string, usd: number): Promise<ConsoleCapSet> {
  const res = await apiClient.post<ConsoleCapSet>(
    `/hub/console/businesses/${encodeURIComponent(tenantId)}/cap`,
    { usd },
  )
  return res.data
}

export async function setConsoleCloseWatch(
  tenantId: string,
  upn: string,
  until: string,
): Promise<ConsoleCloseWatchSet> {
  const res = await apiClient.post<ConsoleCloseWatchSet>(
    `/hub/console/businesses/${encodeURIComponent(tenantId)}/close-watch`,
    { upn, until },
  )
  return res.data
}

export async function endConsoleCloseWatch(
  tenantId: string,
  upn: string,
): Promise<ConsoleCloseWatchEnded> {
  const res = await apiClient.delete<ConsoleCloseWatchEnded>(
    `/hub/console/businesses/${encodeURIComponent(tenantId)}/close-watch/${encodeURIComponent(upn)}`,
  )
  return res.data
}

// BRIEF-F3, support view: one section of a business's own page exactly as
// its owner's /soc/{route} returns it (backend hub.py console_as_owner).
// Staff only, GET only, and every call is logged on the server. Never a
// token, a session or a login as the customer.
export type AsOwnerRoute = 'today' | 'needs-you' | 'actions' | 'people' | 'sources'

async function getAsOwner<T>(
  tenantId: string,
  route: AsOwnerRoute,
  params?: Record<string, unknown>,
): Promise<T> {
  const res = await apiClient.get<T>(
    `/hub/console/businesses/${encodeURIComponent(tenantId)}/as-owner/${route}`,
    params ? { params } : undefined,
  )
  return res.data
}

export function asOwnerRoutes(tenantId: string) {
  return {
    today: () => getAsOwner<SocToday>(tenantId, 'today'),
    needsYou: () => getAsOwner<SocNeedsYou>(tenantId, 'needs-you'),
    actions: (days = 7) => getAsOwner<SocActions>(tenantId, 'actions', { days }),
    people: () => getAsOwner<SocPeople>(tenantId, 'people'),
    sources: () => getAsOwner<SocSources>(tenantId, 'sources'),
  }
}

export default apiClient
